import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useWiki, flattenCategoriesTree } from '../../contexts/WikiContext';
import { Plus, Search, RotateCcw, Trash2, FolderInput } from 'lucide-react';
import WikiPagesTable from './WikiPagesTable';
import SimpleModal from './SimpleModal';
import { useWikiAdminAuth } from './WikiAdminPanel';
import styles from './WikiPagesManager.module.css';

const DEFAULT_SORT = { sortBy: 'updatedAt', sortOrder: 'desc' };

const WikiEditorLazy = React.lazy(() => import('./WikiEditor'));

const WikiPagesManager = () => {
  const {
    getAllPages,
    deletePage,
    updatePage,
    loadPages,
    loadCategories,
    getCategoriesTree,
    getCategories,
    fetchPageBySlug,
  } = useWiki();
  const [filteredPages, setFilteredPages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT.sortBy);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT.sortOrder);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteSelectedConfirm, setDeleteSelectedConfirm] = useState(null);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [deleteSelectedError, setDeleteSelectedError] = useState('');
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveCategoryId, setMoveCategoryId] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');
  const { onAuthError } = useWikiAdminAuth();

  useEffect(() => {
    loadPages();
    loadCategories();
  }, [loadPages, loadCategories]);

  // На mobile/table не хотим фильтровать на каждый символ — небольшая задержка.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery), 180);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const all = getAllPages();
    if (debouncedSearchQuery.trim()) {
      const q = debouncedSearchQuery.toLowerCase();
      setFilteredPages(
        all.filter(
          (p) =>
            (p.title && p.title.toLowerCase().includes(q)) ||
            (p.slug && p.slug.toLowerCase().includes(q))
        )
      );
    } else {
      setFilteredPages(all);
    }
  }, [getAllPages, debouncedSearchQuery]);

  const categoryNameMap = React.useMemo(() => {
    const tree = getCategoriesTree();
    const flat = flattenCategoriesTree(tree);
    const map = new Map();
    flat.forEach((c) => {
      if (c.id != null) map.set(Number(c.id), c.name || '');
      if (c.id != null) map.set(String(c.id), c.name || '');
    });
    return map;
  }, [getCategoriesTree]);

  const sortedPages = React.useMemo(() => {
    const list = filteredPages.map((p) => {
      const categoryId = p.category?.id ?? p.category ?? p.categoryId;
      const categoryDisplayName =
        p.category?.name ?? (categoryId != null ? categoryNameMap.get(categoryId) ?? categoryNameMap.get(String(categoryId)) : null) ?? '—';
      return { ...p, categoryDisplayName };
    });
    const cmp = (a, b) => {
      let v = 0;
      if (sortBy === 'title') {
        v = (a.title || '').localeCompare(b.title || '', 'ru');
      } else if (sortBy === 'updatedAt') {
        v = new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
      } else if (sortBy === 'createdAt') {
        v = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      } else if (sortBy === 'category') {
        v = (a.categoryDisplayName || '').localeCompare(b.categoryDisplayName || '', 'ru');
      }
      return sortOrder === 'asc' ? v : -v;
    };
    list.sort(cmp);
    return list;
  }, [filteredPages, sortBy, sortOrder, categoryNameMap]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(
        field === 'title' || field === 'category' ? 'asc' : 'desc'
      );
    }
  };

  const handleResetSort = () => {
    setSortBy(DEFAULT_SORT.sortBy);
    setSortOrder(DEFAULT_SORT.sortOrder);
  };

  const handleMobileSortByChange = (field) => {
    setSortBy(field);
    setSortOrder(field === 'title' || field === 'category' ? 'asc' : 'desc');
  };

  const handleCreate = () => {
    setEditingPage(null);
    setEditorOpen(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setEditorOpen(true);
  };

  const handleEditorClose = (saved) => {
    setEditorOpen(false);
    setEditingPage(null);
    if (saved) loadPages();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteError('');
    setDeleting(true);
    try {
      await deletePage(deleteConfirm.id);
      setDeleteConfirm(null);
      loadPages();
    } catch (err) {
      if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
        onAuthError(err);
      } else {
        setDeleteError(err.message || 'Ошибка при удалении страницы');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const sid = String(id);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((checked) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(sortedPages.map((p) => String(p.id))));
  }, [sortedPages]);

  const handleDeleteSelected = async () => {
    if (!deleteSelectedConfirm) return;
    setDeleteSelectedError('');
    setDeletingSelected(true);
    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await deletePage(id);
      }
      setSelectedIds(new Set());
      setDeleteSelectedConfirm(null);
      loadPages();
    } catch (err) {
      if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
        onAuthError(err);
      } else {
        setDeleteSelectedError(err.message || 'Ошибка при удалении');
      }
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleMoveToCategory = async () => {
    const categoryId = moveCategoryId != null && moveCategoryId !== '' ? Number(moveCategoryId) : NaN;
    if (!Number.isFinite(categoryId)) {
      setMoveError('Выберите категорию');
      return;
    }
    setMoveError('');
    setMoving(true);
    try {
      const ids = Array.from(selectedIds);
      const pagesToMove = sortedPages.filter((p) => ids.includes(String(p.id)));
      for (const page of pagesToMove) {
        const full = await fetchPageBySlug(page.slug);
        if (full) {
          await updatePage(page.id, {
            title: full.title,
            slug: full.slug,
            content: full.content ?? '',
            category: categoryId,
            author: full.author ?? '',
          });
        }
      }
      setSelectedIds(new Set());
      setMoveModalOpen(false);
      setMoveCategoryId('');
      loadPages();
    } catch (err) {
      if ((err.status === 401 || err.status === 403 || err.isAdminForbidden) && onAuthError) {
        onAuthError(err);
      } else {
        setMoveError(err.message || 'Ошибка при переносе');
      }
    } finally {
      setMoving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Управление страницами</h1>
        <button type="button" className={styles.primaryButton} onClick={handleCreate}>
          <Plus size={20} />
          Создать страницу
        </button>
      </div>

      <div className={styles.searchRow}>
        <div className={styles.searchBar}>
          <Search size={20} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Поиск страниц..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        {(sortBy !== DEFAULT_SORT.sortBy || sortOrder !== DEFAULT_SORT.sortOrder) && (
          <button
            type="button"
            className={styles.resetSortButton}
            onClick={handleResetSort}
            title="Сбросить сортировку"
          >
            <RotateCcw size={18} />
            Сбросить сортировку
          </button>
        )}

        <div className={styles.mobileSortControls}>
          <select
            className={styles.mobileSortSelect}
            value={sortBy}
            onChange={(e) => handleMobileSortByChange(e.target.value)}
            aria-label="Сортировать по"
          >
            <option value="updatedAt">Обновлено</option>
            <option value="createdAt">Создано</option>
            <option value="title">Название</option>
            <option value="category">Категория</option>
          </select>
          <select
            className={styles.mobileSortSelect}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            aria-label="Порядок сортировки"
          >
            <option value="desc">По убыванию</option>
            <option value="asc">По возрастанию</option>
          </select>
        </div>
      </div>

      {filteredPages.length > 0 && (
        <div className={styles.bulkToolbar}>
          <span className={styles.bulkToolbarLabel}>Выбрано: {selectedIds.size}</span>
          <button
            type="button"
            className={styles.dangerButton}
            disabled={selectedIds.size === 0}
            onClick={() => setDeleteSelectedConfirm(selectedIds.size)}
            title="Удалить выбранные страницы"
          >
            <Trash2 size={18} />
            Удалить выбранные
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={selectedIds.size === 0}
            onClick={() => {
              setMoveError('');
              setMoveCategoryId(getCategories()[0]?.id ?? '');
              setMoveModalOpen(true);
            }}
            title="Перенести в категорию"
          >
            <FolderInput size={18} />
            Перенести в категорию
          </button>
        </div>
      )}

      {filteredPages.length > 0 ? (
        <WikiPagesTable
          pages={sortedPages}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onEdit={handleEdit}
          onDelete={setDeleteConfirm}
          formatDate={formatDate}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
        />
      ) : (
        <div className={styles.emptyState}>
          <p>
            {debouncedSearchQuery ? 'Ничего не найдено' : 'Страницы еще не созданы'}
          </p>
          {!debouncedSearchQuery && (
            <button type="button" className={styles.primaryButton} onClick={handleCreate}>
              Создать первую страницу
            </button>
          )}
        </div>
      )}

      {editorOpen && (
        <Suspense fallback={<div className={styles.emptyState}>Загрузка редактора...</div>}>
          <WikiEditorLazy page={editingPage} onClose={handleEditorClose} />
        </Suspense>
      )}

      {deleteConfirm && (
        <SimpleModal
          title="Удалить страницу"
          onClose={() => {
            setDeleteConfirm(null);
            setDeleteError('');
          }}
        >
          <p className={styles.deleteMessage}>
            Вы уверены, что хотите удалить страницу &quot;{deleteConfirm.title}&quot;? Это действие
            нельзя отменить.
          </p>
          {deleteError && (
            <p className={styles.deleteError} role="alert">
              {deleteError}
            </p>
          )}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setDeleteConfirm(null)}
            >
              Отмена
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Удаление...' : 'Удалить'}
            </button>
          </div>
        </SimpleModal>
      )}

      {deleteSelectedConfirm != null && typeof deleteSelectedConfirm === 'number' && (
        <SimpleModal
          title="Удалить выбранные страницы"
          onClose={() => {
            setDeleteSelectedConfirm(null);
            setDeleteSelectedError('');
          }}
        >
          <p className={styles.deleteMessage}>
            Удалить выбранные {deleteSelectedConfirm} страниц? Это действие нельзя отменить.
          </p>
          {deleteSelectedError && (
            <p className={styles.deleteError} role="alert">
              {deleteSelectedError}
            </p>
          )}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setDeleteSelectedConfirm(null)}
            >
              Отмена
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={handleDeleteSelected}
              disabled={deletingSelected}
            >
              {deletingSelected ? 'Удаление...' : 'Удалить'}
            </button>
          </div>
        </SimpleModal>
      )}

      {moveModalOpen && (
        <SimpleModal
          title="Перенести в категорию"
          onClose={() => {
            setMoveModalOpen(false);
            setMoveError('');
          }}
        >
          <div className={styles.moveModalBody}>
            <label className={styles.moveModalLabel} htmlFor="move-category-select">
              Категория:
            </label>
            <select
              id="move-category-select"
              className={styles.moveModalSelect}
              value={moveCategoryId}
              onChange={(e) => setMoveCategoryId(e.target.value)}
            >
              {getCategories().map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {moveError && (
            <p className={styles.deleteError} role="alert">
              {moveError}
            </p>
          )}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setMoveModalOpen(false)}
            >
              Отмена
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleMoveToCategory}
              disabled={moving}
            >
              {moving ? 'Перенос...' : 'Перенести'}
            </button>
          </div>
        </SimpleModal>
      )}
    </div>
  );
};

export default WikiPagesManager;
